/**
 * Acotar una pantalla a la tienda seleccionada.
 *
 * El problema que resuelve
 * -----------------------
 * Casi todas las pantallas del panel necesitan un `store_id` para pedir sus
 * datos, y la tienda seleccionada puede no existir todavia: la tenencia se
 * resuelve con un par de requests (ver `StoreAccessProvider`). Sin un lugar
 * comun, cada pantalla termina escribiendo su propio `if (selected === null)`,
 * y la que se olvide va a pedir `/stores/undefined/...`.
 *
 * `<StoreScoped>` centraliza esa espera y le entrega a la pantalla una tienda
 * **garantizada no nula**, ya sea por render-prop o por `useScopedStore()`.
 *
 * Por que dos formas de acceder a la tienda
 * -----------------------------------------
 * - Render-prop: para pantallas que la reciben como prop (`StoreOverviewScreen`).
 * - `useScopedStore()`: para las pantallas que monta el registro de rutas, que
 *   se montan **sin props** (ver `lazyScreen.tsx`). Es la forma que deberian
 *   usar las pantallas nuevas.
 */

import { createContext, useContext, type ReactNode } from "react";

import type { Store } from "../api/types";
import { useStoreAccess } from "../stores/StoreAccessProvider";
import { ErrorState, NoStoreAccessState, Spinner } from "./Feedback";

const ScopedStoreContext = createContext<Store | null>(null);

export function StoreScoped({
  children,
}: {
  readonly children: (store: Store) => ReactNode;
}) {
  const { state, selected, reload } = useStoreAccess();

  if (state.kind === "idle" || state.kind === "loading") {
    return <Spinner label="Resolviendo tiendas..." />;
  }

  if (state.kind === "error") {
    return <ErrorState error={state.error} onRetry={reload} />;
  }

  // Ready y sin tienda seleccionada solo puede significar una cosa: el servidor
  // no le confirmo ninguna. No es un error ni un "vacio", es falta de acceso.
  if (selected === null) {
    return <NoStoreAccessState />;
  }

  return (
    <ScopedStoreContext.Provider value={selected}>
      {children(selected)}
    </ScopedStoreContext.Provider>
  );
}

/**
 * La tienda seleccionada, garantizada.
 *
 * @throws Si se usa fuera de `<StoreScoped>`. Es deliberado: devolver `null`
 * haria que una pantalla mal ubicada pida datos de "ninguna tienda" en vez de
 * fallar ruidosamente durante el desarrollo.
 */
export function useScopedStore(): Store {
  const store = useContext(ScopedStoreContext);
  if (store === null) {
    throw new Error(
      "useScopedStore() requiere que la ruta este declarada con scope 'store' " +
        "en src/components/routes.tsx",
    );
  }
  return store;
}
